import { Carousel } from '@mantine/carousel'
import { Stack, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react'
import { Entry } from 'contentful'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useErrorMessages } from '@/constants/content'
import useSessionStorage from '@/hooks/useSessionStorage'
import { FEATURED_DAPPS_CONTENT_TYPE } from '@/plugins/contentful/api'
import client from '@/plugins/contentful/client'
import { ErrorMessageDisplay, shuffleArray } from '@/utils/utils'

import UserDetails from '../Details/UserDetails/UserDetails'
import ErrorMessage from '../ErrorMessage'
import itemCardClasses from '../Items/ItemsCard.module.css'
import Loading from '../Loading'
import ImageWrapper from '../MediaWrappers/ImageWrapper'
import classes from './CreatorsCard.module.css'

interface FeaturedDapp {
  dappName: string
  creatorAddress: string
  url: string
  image: string
}

const imageSize = 290

const FeaturedDapps: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const errorMessages = useErrorMessages()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<ErrorMessageDisplay>()
  const [hasFetched, setHasFetched] = useState<boolean>(false)
  const [featuredDappsContent, setFeaturedDappsContent] = useSessionStorage<Entry[]>(
    `featuredDapps`,
    []
  )

  const [dapps, setDapps] = useState<FeaturedDapp[]>([])

  // Carousel state management
  const [slideNumber, setSlideNumber] = useState(0)
  const handleSlideChange = useCallback((index: number) => {
    setSlideNumber(index)
  }, [])

  useEffect(() => {
    if (!featuredDappsContent.length && !hasFetched) {
      setIsLoading(true)
      client
        .getEntries({ content_type: FEATURED_DAPPS_CONTENT_TYPE })
        .then((response: any) => {
          setFeaturedDappsContent(response.items)
          if (response.items.length < 1) {
            setHasFetched(true)
          }
        })
        .catch((e: any) => {
          setError({
            message: errorMessages.cmsConnection,
            details: e.message,
          })
          setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [featuredDappsContent, setFeaturedDappsContent, hasFetched])

  const getDappFromCMS = useCallback(async () => {
    try {
      setIsLoading(true)

      const featuredDapps = featuredDappsContent.map(async item => {
        const { dappName, creatorAddress, url, image } = item.fields

        let imageUrl = ''
        if (
          typeof image === 'object' &&
          image &&
          JSON.parse(JSON.stringify(image))?.fields?.file?.url
        ) {
          imageUrl = JSON.parse(JSON.stringify(image)).fields.file.url
        }

        return {
          dappName,
          creatorAddress,
          url,
          image: imageUrl,
        }
      })

      // Filter out null values
      const collectionTiles = (await Promise.all(featuredDapps)).filter(Boolean) as FeaturedDapp[]

      // Shuffle the array
      const shuffledDappTiles = shuffleArray(collectionTiles)
      setDapps(shuffledDappTiles)
      setIsLoading(false)
    } catch (e: any) {
      console.error(e.message)
      setError({
        message: errorMessages.rpcIdentityConnection,
        details: e.message,
      })
      setIsLoading(false)
    }
  }, [featuredDappsContent])

  useEffect(() => {
    getDappFromCMS()
  }, [featuredDappsContent])

  const handleNavigation = useCallback(
    (index: number) => {
      const dapp = dapps[index]
      if (dapp) {
        window.open(dapp.url, '_blank')
      }
    },
    [dapps, navigate]
  )

  return (
    featuredDappsContent.length > 0 && (
      <Stack style={{ gap: '30px' }}>
        <Title order={isMobile ? 3 : 2}>
          {t(`components.featuredDapps.title`, 'On-Chain Projects')}
        </Title>
        {isLoading && <Loading size="300px" />}
        {error && <ErrorMessage message={error.message} details={error.details} />}
        {!isLoading && !error && (
          <Stack
            style={{
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Carousel
              slideSize="0%"
              previousControlIcon={<IconCaretLeftFilled />}
              nextControlIcon={<IconCaretRightFilled />}
              slidesToScroll="auto"
              onSlideChange={handleSlideChange}
            >
              {dapps.map(
                (dapp, index) =>
                  dapp &&
                  // only load the items that are in the current slideshow window
                  // TODO: make this dynamic based on the screen size (currently 7 item by default)
                  (slideNumber + 1) * 7 > index && (
                    <Carousel.Slide
                      key={`${dapp.url}${String(index)}`}
                      style={{ marginTop: '10px' }}
                    >
                      <Stack
                        style={{ gap: '15px', cursor: 'pointer' }}
                        onClick={() => handleNavigation(index)}
                        className={itemCardClasses.card}
                      >
                        {dapp.image ? (
                          <ImageWrapper
                            src={dapp.image}
                            alt={`Featured Dapp #${index}`}
                            className={classes.image}
                            style={{ width: `${imageSize}px`, height: `${imageSize}px` }}
                            width={imageSize}
                            height={imageSize}
                          />
                        ) : (
                          <Loading size={`${imageSize}px`} />
                        )}
                        {dapp.dappName && (
                          <Stack
                            style={{ alignSelf: 'left', gap: '8px', width: imageSize }}
                            className={itemCardClasses.blockText}
                          >
                            {dapp.creatorAddress && (
                              <UserDetails
                                userId={dapp.creatorAddress}
                                hideCreatorName
                                pfpSize="26px"
                              />
                            )}
                            <Title order={4} style={{ wordBreak: 'break-word' }}>
                              {dapp.dappName}
                            </Title>
                          </Stack>
                        )}
                      </Stack>
                    </Carousel.Slide>
                  )
              )}
            </Carousel>
          </Stack>
        )}
      </Stack>
    )
  )
}

export default FeaturedDapps
